import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="max-w-md w-full border co-hairline bg-white p-8 lg:p-10">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0B]">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} /> Back to login
        </Link>
        {sent ? (
          <div className="mt-6 space-y-4" data-testid="forgot-sent-screen">
            <div className="w-12 h-12 grid place-items-center rounded-sm bg-[#DCFCE7] text-[#15803D]">
              <CheckCircle2 className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <div className="co-label">Check your inbox</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              Reset link sent
            </h1>
            <p className="text-sm text-[#52525B]">
              If an account exists for <b>{email}</b>, a password reset link is on its way.
              The link is valid for 1 hour.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 mt-4 bg-[#0A0A0B] hover:bg-[#27272A] text-white px-4 py-2.5 text-sm font-semibold rounded-sm co-btn-press transition-colors duration-200"
            >
              Return to login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
            <div className="w-12 h-12 grid place-items-center rounded-sm bg-[#ECECFE] text-[#4338CA]">
              <Mail className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <div className="co-label">Forgot password</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              Reset your password
            </h1>
            <p className="text-sm text-[#52525B]">
              Enter the email you use for CollectOne. We&apos;ll send you a link to set a new password.
            </p>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@work.com"
              data-testid="forgot-email-input"
              className="w-full border co-hairline px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
            />
            <button
              type="submit"
              disabled={busy || !email}
              data-testid="forgot-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2.5 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
